import type { EnemyTypeConfig, EnemySpriteConfig, EliteAbility } from "@/types/enemy.types"

// ===== ELITE ABILITY DEFINITIONS =====
// These abilities are only active for elite and unique variants

const ratEliteAbility: EliteAbility = {
  id: "disease-carrier",
  name: "Disease Carrier",
  description: "Soldiers attacking this rat have their attack speed reduced by 15% for 3 seconds.",
  trigger: "passive",
  effect: "slow-attacker",
  value: 0.15, // 15% attack speed reduction
  duration: 180, // 3 seconds at 60fps
}

const goblinEliteAbility: EliteAbility = {
  id: "pillage",
  name: "Pillage",
  description: "Steals 5 gold from the player when it reaches the exit.",
  trigger: "passive",
  effect: "gold-steal",
  value: 5, // 5 gold stolen
}

const mushroomEliteAbility: EliteAbility = {
  id: "spore-cloud",
  name: "Spore Cloud",
  description: "Releases spores on death that slow soldiers and heroes by 25% for 4 seconds.",
  trigger: "on-death",
  effect: "slow-aoe-death",
  value: 0.25, // 25% slow
  duration: 240, // 4 seconds at 60fps
  radius: 80, // pixels
}

const lizardmanEliteAbility: EliteAbility = {
  id: "magic-resistant",
  name: "Magic Resistant",
  description: "Takes 30% less damage from mage towers.",
  trigger: "passive",
  effect: "damage-reduction-mage",
  value: 0.30, // 30% damage reduction
}

const zombieEliteAbility: EliteAbility = {
  id: "toxic-burst",
  name: "Toxic Burst",
  description: "Explodes on death, dealing 15 damage to nearby soldiers.",
  trigger: "on-death",
  effect: "explode-death",
  value: 15, // 15 damage
  radius: 60, // pixels
}

const skeletonEliteAbility: EliteAbility = {
  id: "arrow-deflection",
  name: "Arrow Deflection",
  description: "Takes 30% less damage from archer towers.",
  trigger: "passive",
  effect: "damage-reduction-archer",
  value: 0.30, // 30% damage reduction
}

const orcEliteAbility: EliteAbility = {
  id: "blood-rage",
  name: "Blood Rage",
  description: "Gains +20% attack speed and +10% movement speed when below 50% health.",
  trigger: "passive",
  effect: "blood-rage",
  value: 0.20, // 20% attack speed bonus, 10% movement speed bonus
}

const ghostEliteAbility: EliteAbility = {
  id: "phase-shift",
  name: "Phase Shift",
  description: "When hit, becomes invulnerable for 2 seconds. 10 second cooldown.",
  trigger: "on-damaged",
  effect: "phase-shift",
  value: 1, // unused
  duration: 120, // 2 seconds at 60fps
  cooldown: 600, // 10 seconds at 60fps
}

const werewolfEliteAbility: EliteAbility = {
  id: "lycanthropy",
  name: "Lycanthropy",
  description: "Soldiers killed by this werewolf rise as werewolves that walk to the exit.",
  trigger: "on-hit",
  effect: "lycanthropy",
  value: 1, // unused (effect spawns a werewolf)
}

const golemEliteAbility: EliteAbility = {
  id: "fortified",
  name: "Fortified",
  description: "Takes 50% less damage from archer towers. Regenerates 5 HP/sec.",
  trigger: "passive",
  effect: "damage-reduction-archer",
  value: 0.50, // 50% damage reduction from archers
}

const lichEliteAbility: EliteAbility = {
  id: "dark-resurrection",
  name: "Dark Resurrection",
  description: "Every 12 seconds, resurrects up to 2 dead zombies or skeletons. Resurrected units can't be resurrected again.",
  trigger: "periodic",
  effect: "resurrect-undead",
  value: 2, // max units to resurrect
  cooldown: 720, // 12 seconds at 60fps
  radius: 200, // search radius for corpses
}

const vampireEliteAbility: EliteAbility = {
  id: "life-drain",
  name: "Life Drain",
  description: "Heals for 15% of damage dealt to soldiers, heroes, and summons.",
  trigger: "on-hit",
  effect: "lifesteal",
  value: 0.15, // 15% lifesteal
}

const entEliteAbility: EliteAbility = {
  id: "thick-bark",
  name: "Thick Bark",
  description: "Takes 30% less damage from heroes. Regenerates 15 HP/sec while standing still.",
  trigger: "passive",
  effect: "damage-reduction-hero",
  value: 0.30, // 30% damage reduction from hero
}

const beholderEliteAbility: EliteAbility = {
  id: "nullify-gaze",
  name: "Nullify Gaze",
  description: "Every 5 seconds, disables the nearest tower for 5 seconds.",
  trigger: "periodic",
  effect: "disable-tower",
  value: 1, // unused
  duration: 300, // 5 seconds at 60fps
  cooldown: 300, // 5 seconds at 60fps
}

const demonEliteAbility: EliteAbility = {
  id: "infernal-aura",
  name: "Infernal Aura",
  description: "Nearby towers deal 15% less damage. Immune to slow effects.",
  trigger: "passive",
  effect: "aura-damage-reduction",
  value: 0.15, // 15% damage reduction for nearby towers
  radius: 150, // aura radius
}

const impEliteAbility: EliteAbility = {
  id: "arcane-shield",
  name: "Arcane Shield",
  description: "Takes 30% less damage from siege towers.",
  trigger: "passive",
  effect: "damage-reduction-siege",
  value: 0.30, // 30% damage reduction
}

// ===== ROW MAPPINGS FOR SPRITE DIRECTIONS =====
// Logical direction: 0=up, 1=left, 2=right, 3=down
// Standard row mapping for most enemy sprites (row0=north, row1=south, row2=west, row3=east)
// Maps: up(0)→row0(north), left(1)→row2(west), right(2)→row3(east), down(3)→row1(south)
const standardRowMapping: [number, number, number, number] = [0, 2, 3, 1]

// Row mapping for enemies with north/south swapped in spritesheet
// Maps: up(0)→row1(south), left(1)→row2(west), right(2)→row3(east), down(3)→row0(north)
const flippedNorthSouthRowMapping: [number, number, number, number] = [1, 2, 3, 0]

// ===== HELPER FUNCTION FOR SPRITE CONFIGS =====
// Standard frame counts: idle=4, walk=6, run=8, attack=5, hurt=4, death=6
const createSpriteConfig = (
  baseName: string,
  variant: number,
  scale: number = 1.0,
  offsetY: number = 8,
  hasRun: boolean = true,
  suffix: string = "",
  rowMapping: [number, number, number, number] = standardRowMapping
): EnemySpriteConfig => {
  const base = `/images/enemies/${baseName}${variant}`
  const s = suffix // e.g., "0" for Goblin1
  return {
    idle: { sheet: `${base}_Idle${s}_with_shadow.png`, frameWidth: 64, frameHeight: 64, frameCount: 4, rowCount: 4 },
    walk: { sheet: `${base}_Walk${s}_with_shadow.png`, frameWidth: 64, frameHeight: 64, frameCount: 6, rowCount: 4 },
    ...(hasRun && { run: { sheet: `${base}_Run${s}_with_shadow.png`, frameWidth: 64, frameHeight: 64, frameCount: 8, rowCount: 4 } }),
    attack: { sheet: `${base}_Attack${s}_with_shadow.png`, frameWidth: 64, frameHeight: 64, frameCount: 5, rowCount: 4 },
    hurt: { sheet: `${base}_Hurt${s}_with_shadow.png`, frameWidth: 64, frameHeight: 64, frameCount: 4, rowCount: 4 },
    death: { sheet: `${base}_Death${s}_with_shadow.png`, frameWidth: 64, frameHeight: 64, frameCount: 6, rowCount: 4 },
    scale,
    offsetY,
    rowMapping,
  }
}

// ===== RAT SPRITE CONFIGURATIONS (Level 1) =====
// Rat spritesheets have non-standard frame counts, row layout, and frame size (128x128 vs standard 64x64):
// Frame counts: idle=6, walk=6, run=6, attack=8, hurt=4, death=5
// Row layout: 0=south, 1=north, 2=west, 3=east (different from standard 0=up,1=left,2=right,3=down)
// rowMapping converts logical direction (0=up,1=left,2=right,3=down) to sprite row
const ratRowMapping: [number, number, number, number] = [1, 2, 3, 0] // up→row1, left→row2, right→row3, down→row0

const rat1SpriteConfig: EnemySpriteConfig = {
  idle: { sheet: "/images/enemies/Rat1_Idle_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 6, rowCount: 4 },
  walk: { sheet: "/images/enemies/Rat1_Walk_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 6, rowCount: 4 },
  run: { sheet: "/images/enemies/Rat1_Run_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 6, rowCount: 4 },
  attack: { sheet: "/images/enemies/Rat1_Attack_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 8, rowCount: 4 },
  hurt: { sheet: "/images/enemies/Rat1_Hurt_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 4, rowCount: 4 },
  death: { sheet: "/images/enemies/Rat1_Death_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 5, rowCount: 4 },
  scale: 0.9,
  offsetY: 8,
  rowMapping: ratRowMapping,
}
const rat2SpriteConfig: EnemySpriteConfig = {
  idle: { sheet: "/images/enemies/Rat2_Idle_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 6, rowCount: 4 },
  walk: { sheet: "/images/enemies/Rat2_Walk_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 6, rowCount: 4 },
  run: { sheet: "/images/enemies/Rat2_Run_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 6, rowCount: 4 },
  attack: { sheet: "/images/enemies/Rat2_Attack_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 8, rowCount: 4 },
  hurt: { sheet: "/images/enemies/Rat2_Hurt_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 4, rowCount: 4 },
  death: { sheet: "/images/enemies/Rat2_Death_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 5, rowCount: 4 },
  scale: 1.0,
  offsetY: 8,
  rowMapping: ratRowMapping,
}
const rat3SpriteConfig: EnemySpriteConfig = {
  idle: { sheet: "/images/enemies/Rat3_Idle_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 6, rowCount: 4 },
  walk: { sheet: "/images/enemies/Rat3_Walk_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 6, rowCount: 4 },
  run: { sheet: "/images/enemies/Rat3_Run_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 6, rowCount: 4 },
  attack: { sheet: "/images/enemies/Rat3_Attack_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 8, rowCount: 4 },
  hurt: { sheet: "/images/enemies/Rat3_Hurt_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 4, rowCount: 4 },
  death: { sheet: "/images/enemies/Rat3_Death_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 5, rowCount: 4 },
  scale: 1.1,
  offsetY: 8,
  rowMapping: ratRowMapping,
}

// ===== GOBLIN SPRITE CONFIGURATIONS (Level 2) =====
// Goblin1 has special "0" suffix in filenames
const goblin1SpriteConfig: EnemySpriteConfig = {
  idle: { sheet: "/images/enemies/Goblin1_Idle0_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 4, rowCount: 4 },
  walk: { sheet: "/images/enemies/Goblin1_Walk0_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 6, rowCount: 4 },
  run: { sheet: "/images/enemies/Goblin1_Run0_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 8, rowCount: 4 },
  attack: { sheet: "/images/enemies/Goblin1_Attack0_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 5, rowCount: 4 },
  walkAttack: { sheet: "/images/enemies/Goblin1_Walk_Attack0_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 6, rowCount: 4 },
  runAttack: { sheet: "/images/enemies/Goblin1_Run_Attack0_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 8, rowCount: 4 },
  hurt: { sheet: "/images/enemies/Goblin1_Hurt0_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 4, rowCount: 4 },
  death: { sheet: "/images/enemies/Goblin1_Death0_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 6, rowCount: 4 },
  scale: 1.0,
  offsetY: 8,
  rowMapping: flippedNorthSouthRowMapping,
}
const goblin2SpriteConfig: EnemySpriteConfig = {
  idle: { sheet: "/images/enemies/Goblin2_Idle_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 4, rowCount: 4 },
  walk: { sheet: "/images/enemies/Goblin2_Walk_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 6, rowCount: 4 },
  run: { sheet: "/images/enemies/Goblin2_Run_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 8, rowCount: 4 },
  attack: { sheet: "/images/enemies/Goblin2_Attack_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 5, rowCount: 4 },
  walkAttack: { sheet: "/images/enemies/Goblin2_Walk_attack_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 6, rowCount: 4 },
  runAttack: { sheet: "/images/enemies/Goblin2_Run_attack_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 8, rowCount: 4 },
  hurt: { sheet: "/images/enemies/Goblin2_Hurt_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 4, rowCount: 4 },
  death: { sheet: "/images/enemies/Goblin2_Death_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 6, rowCount: 4 },
  scale: 1.05,
  offsetY: 8,
  rowMapping: flippedNorthSouthRowMapping,
}
const goblin3SpriteConfig: EnemySpriteConfig = {
  idle: { sheet: "/images/enemies/Goblin3_Idle_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 4, rowCount: 4 },
  walk: { sheet: "/images/enemies/Goblin3_Walk_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 6, rowCount: 4 },
  run: { sheet: "/images/enemies/Goblin3_Run_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 8, rowCount: 4 },
  attack: { sheet: "/images/enemies/Goblin3_Attack_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 5, rowCount: 4 },
  walkAttack: { sheet: "/images/enemies/Goblin3_Walk_attack_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 6, rowCount: 4 },
  runAttack: { sheet: "/images/enemies/Goblin3_Run_attack_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 8, rowCount: 4 },
  hurt: { sheet: "/images/enemies/Goblin3_Hurt_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 4, rowCount: 4 },
  death: { sheet: "/images/enemies/Goblin3_Death_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 6, rowCount: 4 },
  scale: 1.1,
  offsetY: 8,
  rowMapping: flippedNorthSouthRowMapping,
}

// ===== MUSHROOM SPRITE CONFIGURATIONS (Level 3) =====
// Mushrooms have north/south swapped in spritesheet
const mushroom1SpriteConfig = createSpriteConfig("Mushroom", 1, 0.95, 8, true, "", flippedNorthSouthRowMapping)
const mushroom2SpriteConfig = createSpriteConfig("Mushroom", 2, 1.05, 8, true, "", flippedNorthSouthRowMapping)
const mushroom3SpriteConfig = createSpriteConfig("Mushroom", 3, 1.15, 8, true, "", flippedNorthSouthRowMapping)

// ===== LIZARDMAN SPRITE CONFIGURATIONS (Level 4) =====
// Lizardmen have north/south swapped in spritesheet
const lizardman1SpriteConfig = createSpriteConfig("Lizardman", 1, 1.0, 8, true, "", flippedNorthSouthRowMapping)
const lizardman2SpriteConfig = createSpriteConfig("Lizardman", 2, 1.1, 8, true, "", flippedNorthSouthRowMapping)
const lizardman3SpriteConfig = createSpriteConfig("Lizardman", 3, 1.2, 8, true, "", flippedNorthSouthRowMapping)

// ===== ZOMBIE SPRITE CONFIGURATIONS (Level 5) =====
const zombie1SpriteConfig = createSpriteConfig("Zombie", 1, 1.0, 8)
const zombie2SpriteConfig = createSpriteConfig("Zombie", 2, 1.1, 8)
const zombie3SpriteConfig = createSpriteConfig("Zombie", 3, 1.2, 8)

// ===== SKELETON SPRITE CONFIGURATIONS (Level 5.5) =====
const skeleton1SpriteConfig = createSpriteConfig("Skeleton", 1, 1.0, 8)
const skeleton2SpriteConfig = createSpriteConfig("Skeleton", 2, 1.05, 8)
const skeleton3SpriteConfig = createSpriteConfig("Skeleton", 3, 1.1, 8)

// ===== ORC SPRITE CONFIGURATIONS (Level 6) =====
// Orc sprites are lowercase, and have north/south swapped in spritesheet
const orc1SpriteConfig: EnemySpriteConfig = {
  idle: { sheet: "/images/enemies/orc1_idle_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 4, rowCount: 4 },
  walk: { sheet: "/images/enemies/orc1_walk_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 6, rowCount: 4 },
  run: { sheet: "/images/enemies/orc1_run_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 8, rowCount: 4 },
  attack: { sheet: "/images/enemies/orc1_attack_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 5, rowCount: 4 },
  hurt: { sheet: "/images/enemies/orc1_hurt_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 4, rowCount: 4 },
  death: { sheet: "/images/enemies/orc1_death_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 6, rowCount: 4 },
  scale: 1.0,
  offsetY: 8,
  rowMapping: flippedNorthSouthRowMapping,
}
const orc2SpriteConfig: EnemySpriteConfig = {
  idle: { sheet: "/images/enemies/orc2_idle_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 4, rowCount: 4 },
  walk: { sheet: "/images/enemies/orc2_walk_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 6, rowCount: 4 },
  run: { sheet: "/images/enemies/orc2_run_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 8, rowCount: 4 },
  attack: { sheet: "/images/enemies/orc2_attack_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 5, rowCount: 4 },
  hurt: { sheet: "/images/enemies/orc2_hurt_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 4, rowCount: 4 },
  death: { sheet: "/images/enemies/orc2_death_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 6, rowCount: 4 },
  scale: 1.1,
  offsetY: 8,
  rowMapping: flippedNorthSouthRowMapping,
}
const orc3SpriteConfig: EnemySpriteConfig = {
  idle: { sheet: "/images/enemies/orc3_idle_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 4, rowCount: 4 },
  walk: { sheet: "/images/enemies/orc3_walk_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 6, rowCount: 4 },
  run: { sheet: "/images/enemies/orc3_run_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 8, rowCount: 4 },
  attack: { sheet: "/images/enemies/orc3_attack_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 5, rowCount: 4 },
  hurt: { sheet: "/images/enemies/orc3_hurt_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 4, rowCount: 4 },
  death: { sheet: "/images/enemies/orc3_death_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 6, rowCount: 4 },
  scale: 1.2,
  offsetY: 8,
  rowMapping: flippedNorthSouthRowMapping,
}

// ===== GHOST SPRITE CONFIGURATIONS (Level 7) =====
// Ghosts have north/south swapped in spritesheet
const ghost1SpriteConfig = createSpriteConfig("Ghost", 1, 1.0, 8, true, "", flippedNorthSouthRowMapping)
const ghost2SpriteConfig = createSpriteConfig("Ghost", 2, 1.1, 8, true, "", flippedNorthSouthRowMapping)
const ghost3SpriteConfig = createSpriteConfig("Ghost", 3, 1.2, 8, true, "", flippedNorthSouthRowMapping)

// ===== GNOLL/WEREWOLF SPRITE CONFIGURATIONS (Level 8) =====
// Gnoll1 uses "Gnoll_Death" instead of "Gnoll1_Death"
// Gnolls have north/south swapped in spritesheet
const gnoll1SpriteConfig: EnemySpriteConfig = {
  idle: { sheet: "/images/enemies/Gnoll1_Idle_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 4, rowCount: 4 },
  walk: { sheet: "/images/enemies/Gnoll1_Walk_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 6, rowCount: 4 },
  run: { sheet: "/images/enemies/Gnoll1_Run_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 8, rowCount: 4 },
  attack: { sheet: "/images/enemies/Gnoll1_Attack_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 5, rowCount: 4 },
  hurt: { sheet: "/images/enemies/Gnoll1_Hurt_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 4, rowCount: 4 },
  death: { sheet: "/images/enemies/Gnoll_Death_with_shadow.png", frameWidth: 64, frameHeight: 64, frameCount: 6, rowCount: 4 },
  scale: 1.0,
  offsetY: 8,
  rowMapping: flippedNorthSouthRowMapping,
}
const gnoll2SpriteConfig = createSpriteConfig("Gnoll", 2, 1.1, 8, true, "", flippedNorthSouthRowMapping)
const gnoll3SpriteConfig = createSpriteConfig("Gnoll", 3, 1.2, 8, true, "", flippedNorthSouthRowMapping)

// ===== GOLEM SPRITE CONFIGURATIONS (Level 9) =====
// Golem spritesheets have non-standard frame size (128x128 vs standard 64x64) and frame counts:
// Frame counts: idle=4, walk=8, run=8, attack=9, hurt=4, death=8
const golem1SpriteConfig: EnemySpriteConfig = {
  idle: { sheet: "/images/enemies/Golem1_Idle_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 4, rowCount: 4 },
  walk: { sheet: "/images/enemies/Golem1_Walk_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 8, rowCount: 4 },
  run: { sheet: "/images/enemies/Golem1_Run_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 8, rowCount: 4 },
  attack: { sheet: "/images/enemies/Golem1_Attack_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 9, rowCount: 4 },
  hurt: { sheet: "/images/enemies/Golem1_Hurt_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 4, rowCount: 4 },
  death: { sheet: "/images/enemies/Golem1_Death_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 8, rowCount: 4 },
  scale: 1.1,
  offsetY: 8,
  rowMapping: standardRowMapping,
}
const golem2SpriteConfig: EnemySpriteConfig = {
  idle: { sheet: "/images/enemies/Golem2_Idle_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 4, rowCount: 4 },
  walk: { sheet: "/images/enemies/Golem2_Walk_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 8, rowCount: 4 },
  run: { sheet: "/images/enemies/Golem2_Run_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 8, rowCount: 4 },
  attack: { sheet: "/images/enemies/Golem2_Attack_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 9, rowCount: 4 },
  hurt: { sheet: "/images/enemies/Golem2_Hurt_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 4, rowCount: 4 },
  death: { sheet: "/images/enemies/Golem2_Death_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 8, rowCount: 4 },
  scale: 1.2,
  offsetY: 8,
  rowMapping: standardRowMapping,
}
const golem3SpriteConfig: EnemySpriteConfig = {
  idle: { sheet: "/images/enemies/Golem3_Idle_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 4, rowCount: 4 },
  walk: { sheet: "/images/enemies/Golem3_Walk_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 8, rowCount: 4 },
  run: { sheet: "/images/enemies/Golem3_Run_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 8, rowCount: 4 },
  attack: { sheet: "/images/enemies/Golem3_Attack_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 9, rowCount: 4 },
  hurt: { sheet: "/images/enemies/Golem3_Hurt_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 4, rowCount: 4 },
  death: { sheet: "/images/enemies/Golem3_Death_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 8, rowCount: 4 },
  scale: 1.3,
  offsetY: 8,
  rowMapping: standardRowMapping,
}

// ===== LICH SPRITE CONFIGURATIONS (Level 10) =====
// Liches have north/south swapped in spritesheet
const lich1SpriteConfig = createSpriteConfig("Lich", 1, 1.0, 8, true, "", flippedNorthSouthRowMapping)
const lich2SpriteConfig = createSpriteConfig("Lich", 2, 1.1, 8, true, "", flippedNorthSouthRowMapping)
const lich3SpriteConfig = createSpriteConfig("Lich", 3, 1.2, 8, true, "", flippedNorthSouthRowMapping)

// ===== VAMPIRE SPRITE CONFIGURATIONS (Level 11) =====
// Vampire sprites use "Vampires" (plural) in filename
// Vampires have north/south swapped in spritesheet
const vampire1SpriteConfig = createSpriteConfig("Vampires", 1, 1.0, 8, true, "", flippedNorthSouthRowMapping)
const vampire2SpriteConfig = createSpriteConfig("Vampires", 2, 1.1, 8, true, "", flippedNorthSouthRowMapping)
const vampire3SpriteConfig = createSpriteConfig("Vampires", 3, 1.2, 8, true, "", flippedNorthSouthRowMapping)

// ===== ENT SPRITE CONFIGURATIONS (Level 12) =====
// Ent spritesheets have non-standard frame size (128x128 vs standard 64x64) and frame counts:
// Frame counts: idle=4, walk=6, run=8, attack=7, hurt=4, death=6
const ent1SpriteConfig: EnemySpriteConfig = {
  idle: { sheet: "/images/enemies/Ent1_Idle_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 4, rowCount: 4 },
  walk: { sheet: "/images/enemies/Ent1_Walk_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 6, rowCount: 4 },
  run: { sheet: "/images/enemies/Ent1_Run_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 8, rowCount: 4 },
  attack: { sheet: "/images/enemies/Ent1_Attack_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 7, rowCount: 4 },
  hurt: { sheet: "/images/enemies/Ent1_Hurt_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 4, rowCount: 4 },
  death: { sheet: "/images/enemies/Ent1_Death_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 6, rowCount: 4 },
  scale: 1.2,
  offsetY: 8,
  rowMapping: standardRowMapping,
}
const ent2SpriteConfig: EnemySpriteConfig = {
  idle: { sheet: "/images/enemies/Ent2_Idle_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 4, rowCount: 4 },
  walk: { sheet: "/images/enemies/Ent2_Walk_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 6, rowCount: 4 },
  run: { sheet: "/images/enemies/Ent2_Run_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 8, rowCount: 4 },
  attack: { sheet: "/images/enemies/Ent2_Attack_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 7, rowCount: 4 },
  hurt: { sheet: "/images/enemies/Ent2_Hurt_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 4, rowCount: 4 },
  death: { sheet: "/images/enemies/Ent2_Death_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 6, rowCount: 4 },
  scale: 1.3,
  offsetY: 8,
  rowMapping: standardRowMapping,
}
const ent3SpriteConfig: EnemySpriteConfig = {
  idle: { sheet: "/images/enemies/Ent3_Idle_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 4, rowCount: 4 },
  walk: { sheet: "/images/enemies/Ent3_Walk_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 6, rowCount: 4 },
  run: { sheet: "/images/enemies/Ent3_Run_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 8, rowCount: 4 },
  attack: { sheet: "/images/enemies/Ent3_Attack_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 7, rowCount: 4 },
  hurt: { sheet: "/images/enemies/Ent3_Hurt_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 4, rowCount: 4 },
  death: { sheet: "/images/enemies/Ent3_Death_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 6, rowCount: 4 },
  scale: 1.4,
  offsetY: 8,
  rowMapping: standardRowMapping,
}

// ===== BEHOLDER SPRITE CONFIGURATIONS (Level 13) =====
// Beholders have north/south swapped in spritesheet
const beholder1SpriteConfig = createSpriteConfig("Beholder", 1, 1.1, 8, true, "", flippedNorthSouthRowMapping)
const beholder2SpriteConfig = createSpriteConfig("Beholder", 2, 1.2, 8, true, "", flippedNorthSouthRowMapping)
const beholder3SpriteConfig = createSpriteConfig("Beholder", 3, 1.3, 8, true, "", flippedNorthSouthRowMapping)

// ===== DEMON SPRITE CONFIGURATIONS (Level 14) =====
// Demon spritesheets have non-standard frame size (128x128 vs standard 64x64) and frame counts:
// Frame counts: idle=4, walk=6, run=8, attack=10, hurt=4, death=13
// Demons have north/south swapped in spritesheet
const demon1SpriteConfig: EnemySpriteConfig = {
  idle: { sheet: "/images/enemies/Demon1_Idle_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 4, rowCount: 4 },
  walk: { sheet: "/images/enemies/Demon1_Walk_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 6, rowCount: 4 },
  run: { sheet: "/images/enemies/Demon1_Run_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 8, rowCount: 4 },
  attack: { sheet: "/images/enemies/Demon1_Attack_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 10, rowCount: 4 },
  hurt: { sheet: "/images/enemies/Demon1_Hurt_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 4, rowCount: 4 },
  death: { sheet: "/images/enemies/Demon1_Death_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 13, rowCount: 4 },
  scale: 1.2,
  offsetY: 8,
  rowMapping: flippedNorthSouthRowMapping,
}
const demon2SpriteConfig: EnemySpriteConfig = {
  idle: { sheet: "/images/enemies/Demon2_Idle_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 4, rowCount: 4 },
  walk: { sheet: "/images/enemies/Demon2_Walk_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 6, rowCount: 4 },
  run: { sheet: "/images/enemies/Demon2_Run_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 8, rowCount: 4 },
  attack: { sheet: "/images/enemies/Demon2_Attack_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 10, rowCount: 4 },
  hurt: { sheet: "/images/enemies/Demon2_Hurt_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 4, rowCount: 4 },
  death: { sheet: "/images/enemies/Demon2_Death_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 13, rowCount: 4 },
  scale: 1.3,
  offsetY: 8,
  rowMapping: flippedNorthSouthRowMapping,
}
const demon3SpriteConfig: EnemySpriteConfig = {
  idle: { sheet: "/images/enemies/Demon3_Idle_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 4, rowCount: 4 },
  walk: { sheet: "/images/enemies/Demon3_Walk_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 6, rowCount: 4 },
  run: { sheet: "/images/enemies/Demon3_Run_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 8, rowCount: 4 },
  attack: { sheet: "/images/enemies/Demon3_Attack_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 10, rowCount: 4 },
  hurt: { sheet: "/images/enemies/Demon3_Hurt_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 4, rowCount: 4 },
  death: { sheet: "/images/enemies/Demon3_Death_with_shadow.png", frameWidth: 128, frameHeight: 128, frameCount: 13, rowCount: 4 },
  scale: 1.4,
  offsetY: 8,
  rowMapping: flippedNorthSouthRowMapping,
}

// ===== IMP SPRITE CONFIGURATIONS (Level 14 - mixed with Demon) =====
// Imps have north/south swapped in spritesheet
const imp1SpriteConfig = createSpriteConfig("Imp", 1, 0.9, 8, true, "", flippedNorthSouthRowMapping)
const imp2SpriteConfig = createSpriteConfig("Imp", 2, 1.0, 8, true, "", flippedNorthSouthRowMapping)
const imp3SpriteConfig = createSpriteConfig("Imp", 3, 1.1, 8, true, "", flippedNorthSouthRowMapping)


// ===== ENEMY TYPE DEFINITIONS =====
export const enemyTypes: Record<string, EnemyTypeConfig> = {
  // ===== LEVEL 1 - RATS (Nature Theme - Weakest) =====
  rat: {
    id: "rat",
    name: "Rat",
    displayName: "Sewer Rat",
    class: "fodder",
    level: 1,
    behavior: "ground-melee",
    stats: {
      health: 50,
      speed: 1.8,
      damage: { min: 4, max: 6 },
      attackRange: 28,
      attackSpeed: 60, // 1 attack per second (~5 DPS)
      attackStyle: "melee",
      damageType: "normal",
      defenseType: "light",
      armor: 0,
      reward: 10,
      size: 16,
      experienceReward: 20,
      leavesCorpse: true,
    },
    color: "#8B7355",
    portraitColor: "#5C4D3A",
    description: "Common vermin from the sewers. Weak but numerous.",
    spriteConfig: rat1SpriteConfig,
    variants: {
      elite: { spriteConfig: rat2SpriteConfig, name: "Plague Rat", displayName: "Plague Rat" },
      unique: { spriteConfig: rat3SpriteConfig, name: "Rat King", displayName: "Rat King" },
    },
    eliteAbility: ratEliteAbility,
  },

  // ===== LEVEL 2 - GOBLINS (Humanoid Theme) =====
  goblin: {
    id: "goblin",
    name: "Goblin",
    displayName: "Goblin Scout",
    class: "fodder",
    level: 2,
    behavior: "ground-melee",
    stats: {
      health: 75,
      speed: 1.9,
      damage: { min: 6, max: 8 },
      attackRange: 28,
      attackSpeed: 54, // ~1.1 attacks per second (~8 DPS)
      attackStyle: "melee",
      damageType: "normal",
      defenseType: "light",
      armor: 1,
      reward: 15,
      size: 18,
      experienceReward: 25,
      leavesCorpse: true,
    },
    color: "#7B9B3B",
    portraitColor: "#5A7B2B",
    description: "A small but vicious creature. Goblins travel in packs.",
    spriteConfig: goblin1SpriteConfig,
    variants: {
      elite: { spriteConfig: goblin2SpriteConfig, name: "Goblin Raider", displayName: "Goblin Raider" },
      unique: { spriteConfig: goblin3SpriteConfig, name: "Goblin Warlord", displayName: "Goblin Warlord" },
    },
    eliteAbility: goblinEliteAbility,
  },

  // ===== LEVEL 3 - MUSHROOMS (Nature Theme) =====
  mushroom: {
    id: "mushroom",
    name: "Mushroom",
    displayName: "Spore Walker",
    class: "fodder",
    level: 3,
    behavior: "ground-melee",
    stats: {
      health: 100,
      speed: 1.8,
      damage: { min: 8, max: 12 },
      attackRange: 30,
      attackSpeed: 50, // 1.2 attacks per second (~12 DPS)
      attackStyle: "melee",
      damageType: "normal",
      defenseType: "medium",
      armor: 1,
      reward: 25,
      size: 20,
      experienceReward: 35,
      leavesCorpse: true,
    },
    color: "#8B4513",
    portraitColor: "#5C2E0D",
    description: "Ambulatory fungi animated by dark spores. Releases toxins on death.",
    spriteConfig: mushroom1SpriteConfig,
    variants: {
      elite: { spriteConfig: mushroom2SpriteConfig, name: "Toxic Shroom", displayName: "Toxic Shroom" },
      unique: { spriteConfig: mushroom3SpriteConfig, name: "Myconic Tyrant", displayName: "Myconic Tyrant" },
    },
    eliteAbility: mushroomEliteAbility,
  },

  // ===== LEVEL 4 - LIZARDMEN (Humanoid Theme) =====
  lizardman: {
    id: "lizardman",
    name: "Lizardman",
    displayName: "Lizard Scout",
    class: "soldier",
    level: 4,
    behavior: "ground-melee",
    stats: {
      health: 150,
      speed: 2.0,
      damage: { min: 12, max: 18 },
      attackRange: 32,
      attackSpeed: 48, // 1.25 attacks per second (~19 DPS)
      attackStyle: "melee",
      damageType: "normal",
      defenseType: "medium",
      armor: 2,
      reward: 40,
      size: 22,
      experienceReward: 50,
      leavesCorpse: true,
    },
    color: "#228B22",
    portraitColor: "#165C16",
    description: "Cold-blooded warriors from the swamps. Skilled with spears.",
    spriteConfig: lizardman1SpriteConfig,
    variants: {
      elite: { spriteConfig: lizardman2SpriteConfig, name: "Lizard Warrior", displayName: "Lizard Warrior" },
      unique: { spriteConfig: lizardman3SpriteConfig, name: "Lizard Chieftain", displayName: "Lizard Chieftain" },
    },
    eliteAbility: lizardmanEliteAbility,
  },

  // ===== LEVEL 5 - ZOMBIES (Undead Theme) =====
  zombie: {
    id: "zombie",
    name: "Zombie",
    displayName: "Shambling Corpse",
    class: "soldier",
    level: 5,
    behavior: "ground-melee",
    stats: {
      health: 200,
      speed: 1.8,
      damage: { min: 18, max: 25 },
      attackRange: 30,
      attackSpeed: 54, // ~1.1 attacks per second (~24 DPS) - Zombies are slow
      attackStyle: "melee",
      damageType: "normal",
      defenseType: "heavy",
      armor: 2,
      reward: 60,
      size: 24,
      experienceReward: 70,
      leavesCorpse: false,
    },
    color: "#556B2F",
    portraitColor: "#3D4D21",
    description: "The walking dead. Slow but relentless in their pursuit.",
    spriteConfig: zombie1SpriteConfig,
    variants: {
      elite: { spriteConfig: zombie2SpriteConfig, name: "Bloated Corpse", displayName: "Bloated Corpse" },
      unique: { spriteConfig: zombie3SpriteConfig, name: "Zombie Lord", displayName: "Zombie Lord" },
    },
    eliteAbility: zombieEliteAbility,
  },

  // ===== LEVEL 5.5 - SKELETONS (Undead Theme - mixed with zombies and liches) =====
  skeleton: {
    id: "skeleton",
    name: "Fallen Warrior",
    displayName: "Fallen Warrior",
    class: "soldier",
    level: 5.5,
    behavior: "ground-melee",
    stats: {
      health: 250,
      speed: 2.0,
      damage: { min: 20, max: 28 },
      attackRange: 30,
      attackSpeed: 50,
      attackStyle: "melee",
      damageType: "normal",
      defenseType: "medium",
      armor: 2,
      reward: 70,
      size: 24,
      experienceReward: 80,
      leavesCorpse: false,
    },
    color: "#D2B48C",
    portraitColor: "#A0826D",
    description: "Reanimated warriors from ancient battles. Quick and deadly.",
    spriteConfig: skeleton1SpriteConfig,
    variants: {
      elite: { spriteConfig: skeleton2SpriteConfig, name: "Skeletal Champion", displayName: "Skeletal Champion" },
      unique: { spriteConfig: skeleton3SpriteConfig, name: "Death Knight", displayName: "Death Knight" },
    },
    eliteAbility: skeletonEliteAbility,
  },

  // ===== LEVEL 6 - ORCS (Humanoid Theme) =====
  orc: {
    id: "orc",
    name: "Orc",
    displayName: "Orc Grunt",
    class: "soldier",
    level: 6,
    behavior: "ground-melee",
    stats: {
      health: 300,
      speed: 2.0,
      damage: { min: 25, max: 35 },
      attackRange: 34,
      attackSpeed: 48, // 1.25 attacks per second (~38 DPS)
      attackStyle: "melee",
      damageType: "normal",
      defenseType: "heavy",
      armor: 3,
      reward: 85,
      size: 26,
      experienceReward: 95,
      leavesCorpse: true,
    },
    color: "#4A6B3A",
    portraitColor: "#324A28",
    description: "Brutal warriors bred for war. Strong and aggressive.",
    spriteConfig: orc1SpriteConfig,
    variants: {
      elite: { spriteConfig: orc2SpriteConfig, name: "Orc Berserker", displayName: "Orc Berserker" },
      unique: { spriteConfig: orc3SpriteConfig, name: "Orc Warchief", displayName: "Orc Warchief" },
    },
    eliteAbility: orcEliteAbility,
  },

  // ===== LEVEL 7 - GHOSTS (Undead Theme) =====
  ghost: {
    id: "ghost",
    name: "Ghost",
    displayName: "Restless Spirit",
    class: "elite",
    level: 7,
    behavior: "ground-melee",
    stats: {
      health: 450,
      speed: 2.2,
      damage: { min: 35, max: 50 },
      attackRange: 32,
      attackSpeed: 45, // 1.33 attacks per second (~57 DPS)
      attackStyle: "melee",
      damageType: "magic",
      defenseType: "light",
      armor: 4,
      reward: 115,
      size: 24,
      experienceReward: 125,
      leavesCorpse: false,
    },
    color: "#B0C4DE",
    portraitColor: "#8A9BB0",
    description: "Spectral entities that phase through defenses. Magic damage.",
    spriteConfig: ghost1SpriteConfig,
    variants: {
      elite: { spriteConfig: ghost2SpriteConfig, name: "Phantom", displayName: "Phantom" },
      unique: { spriteConfig: ghost3SpriteConfig, name: "Phantom Lord", displayName: "Phantom Lord" },
    },
    eliteAbility: ghostEliteAbility,
  },

  // ===== LEVEL 8 - GNOLLS/WEREWOLVES (Supernatural Theme) =====
  gnoll: {
    id: "gnoll",
    name: "Werewolf",
    displayName: "Feral Werewolf",
    class: "elite",
    level: 8,
    behavior: "fast-melee",
    stats: {
      health: 650,
      speed: 2.4,
      damage: { min: 50, max: 70 },
      attackRange: 30,
      attackSpeed: 40, // 1.5 attacks per second (~90 DPS) - Werewolves are fast attackers
      attackStyle: "melee",
      damageType: "normal",
      defenseType: "medium",
      armor: 5,
      reward: 150,
      size: 28,
      experienceReward: 160,
      leavesCorpse: true,
    },
    color: "#8B6914",
    portraitColor: "#5C470D",
    description: "Savage lycanthropes that hunt in packs. Fast and deadly.",
    spriteConfig: gnoll1SpriteConfig,
    variants: {
      elite: { spriteConfig: gnoll2SpriteConfig, name: "Alpha Wolf", displayName: "Alpha Wolf" },
      unique: { spriteConfig: gnoll3SpriteConfig, name: "Werewolf Packmaster", displayName: "Werewolf Packmaster" },
    },
    eliteAbility: werewolfEliteAbility,
  },

  // ===== LEVEL 9 - GOLEMS (Supernatural Theme) =====
  golem: {
    id: "golem",
    name: "Golem",
    displayName: "Stone Golem",
    class: "elite",
    level: 9,
    behavior: "tank",
    stats: {
      health: 900,
      speed: 2.0,
      damage: { min: 70, max: 95 },
      attackRange: 36,
      attackSpeed: 60, // 1 attack per second (~83 DPS) - Golems are slow but hit hard
      attackStyle: "melee",
      damageType: "normal",
      defenseType: "fortified",
      armor: 6,
      reward: 190,
      size: 32,
      experienceReward: 200,
      leavesCorpse: false,
    },
    color: "#696969",
    portraitColor: "#484848",
    description: "Animated stone constructs. Incredibly tough but slow.",
    spriteConfig: golem1SpriteConfig,
    variants: {
      elite: { spriteConfig: golem2SpriteConfig, name: "Iron Golem", displayName: "Iron Golem" },
      unique: { spriteConfig: golem3SpriteConfig, name: "Golem Colossus", displayName: "Golem Colossus" },
    },
    eliteAbility: golemEliteAbility,
  },

  // ===== LEVEL 10 - LICHES (Undead Theme) =====
  lich: {
    id: "lich",
    name: "Lich",
    displayName: "Necromancer",
    class: "champion",
    level: 10,
    behavior: "ground-ranged",
    stats: {
      health: 1200,
      mana: 300,
      speed: 1.8,
      damage: { min: 95, max: 130 },
      attackRange: 400,
      attackSpeed: 90, // 0.67 attacks per second (~75 DPS) - Casters are slower
      attackStyle: "ranged",
      damageType: "magic",
      defenseType: "medium",
      armor: 7,
      reward: 240,
      size: 28,
      experienceReward: 250,
      leavesCorpse: false,
    },
    color: "#4B0082",
    portraitColor: "#2D0050",
    description: "Undead sorcerers of immense power. Masters of dark magic.",
    spriteConfig: lich1SpriteConfig,
    projectileColor: "#00FF00", // Green projectiles
    variants: {
      elite: { spriteConfig: lich2SpriteConfig, name: "Elder Lich", displayName: "Elder Lich" },
      unique: { spriteConfig: lich3SpriteConfig, name: "Lich King", displayName: "Lich King" },
    },
    eliteAbility: lichEliteAbility,
  },

  // ===== LEVEL 11 - VAMPIRES (Supernatural Theme) =====
  vampire: {
    id: "vampire",
    name: "Vampire",
    displayName: "Vampire Spawn",
    class: "champion",
    level: 11,
    behavior: "ground-melee",
    stats: {
      health: 1600,
      speed: 2.2,
      damage: { min: 130, max: 175 },
      attackRange: 34,
      attackSpeed: 48, // 1.25 attacks per second (~190 DPS)
      attackStyle: "melee",
      damageType: "normal",
      defenseType: "heavy",
      armor: 8,
      reward: 300,
      size: 28,
      experienceReward: 310,
      leavesCorpse: false,
    },
    color: "#8B0000",
    portraitColor: "#5C0000",
    description: "Undead nobility that drain the life from their victims.",
    spriteConfig: vampire1SpriteConfig,
    variants: {
      elite: { spriteConfig: vampire2SpriteConfig, name: "Vampire Lord", displayName: "Vampire Lord" },
      unique: { spriteConfig: vampire3SpriteConfig, name: "Vampire Ancient", displayName: "Vampire Ancient" },
    },
    eliteAbility: vampireEliteAbility,
  },

  // ===== LEVEL 12 - ENTS (Nature Theme - Late game nature) =====
  ent: {
    id: "ent",
    name: "Ent",
    displayName: "Young Treant",
    class: "champion",
    level: 12,
    behavior: "tank",
    stats: {
      health: 2100,
      speed: 1.6,
      damage: { min: 175, max: 230 },
      attackRange: 40,
      attackSpeed: 72, // 0.83 attacks per second (~170 DPS) - Ents are slow
      attackStyle: "melee",
      damageType: "normal",
      defenseType: "fortified",
      armor: 10,
      reward: 370,
      size: 38,
      experienceReward: 380,
      leavesCorpse: false,
    },
    color: "#228B22",
    portraitColor: "#165C16",
    description: "Ancient tree spirits awakened to war. Incredibly durable.",
    spriteConfig: ent1SpriteConfig,
    variants: {
      elite: { spriteConfig: ent2SpriteConfig, name: "Ancient Treant", displayName: "Ancient Treant" },
      unique: { spriteConfig: ent3SpriteConfig, name: "Ancient One", displayName: "Ancient One" },
    },
    eliteAbility: entEliteAbility,
  },

  // ===== LEVEL 13 - BEHOLDERS (Supernatural Theme) =====
  beholder: {
    id: "beholder",
    name: "Beholder",
    displayName: "Eye Tyrant",
    class: "champion",
    level: 13,
    behavior: "ground-ranged",
    stats: {
      health: 2800,
      mana: 500,
      speed: 2.0,
      damage: { min: 230, max: 300 },
      attackRange: 450,
      attackSpeed: 80, // 0.75 attacks per second (~200 DPS) - Casters are slower
      attackStyle: "ranged",
      damageType: "magic",
      defenseType: "heavy",
      armor: 12,
      reward: 450,
      size: 36,
      experienceReward: 460,
      leavesCorpse: false,
    },
    color: "#8B4513",
    portraitColor: "#5C2E0D",
    description: "Aberrant creatures with deadly eye rays. Highly intelligent.",
    spriteConfig: beholder1SpriteConfig,
    projectileColor: "#FFFF00", // Yellow projectiles
    variants: {
      elite: { spriteConfig: beholder2SpriteConfig, name: "Elder Eye", displayName: "Elder Eye" },
      unique: { spriteConfig: beholder3SpriteConfig, name: "Eye of Doom", displayName: "Eye of Doom" },
    },
    eliteAbility: beholderEliteAbility,
  },

  // ===== LEVEL 14 - DEMONS (Supernatural Theme - Final) =====
  demon: {
    id: "demon",
    name: "Demon",
    displayName: "Hellspawn",
    class: "champion",
    level: 14,
    behavior: "ground-melee",
    stats: {
      health: 3500,
      mana: 400,
      speed: 2.2,
      damage: { min: 300, max: 400 },
      attackRange: 38,
      attackSpeed: 54, // ~1.1 attacks per second (~385 DPS) - Demons hit hard
      attackStyle: "melee",
      damageType: "chaos",
      defenseType: "fortified",
      armor: 15,
      reward: 550,
      size: 40,
      experienceReward: 560,
      leavesCorpse: false,
    },
    color: "#8B0000",
    portraitColor: "#5C0000",
    description: "Fiends from the burning hells. The most fearsome enemies.",
    spriteConfig: demon1SpriteConfig,
    variants: {
      elite: { spriteConfig: demon2SpriteConfig, name: "Pit Lord", displayName: "Pit Lord" },
      unique: { spriteConfig: demon3SpriteConfig, name: "Demon Lord", displayName: "Demon Lord" },
    },
    eliteAbility: demonEliteAbility,
  },

  // ===== IMP (Level 14 - Mixed with Demons) =====
  imp: {
    id: "imp",
    name: "Imp",
    displayName: "Lesser Imp",
    class: "soldier",
    level: 14,
    behavior: "ground-ranged",
    stats: {
      health: 2000,
      mana: 200,
      speed: 2.4,
      damage: { min: 200, max: 280 },
      attackRange: 350,
      attackSpeed: 72, // 0.83 attacks per second (~200 DPS) - Imps cast spells
      attackStyle: "ranged",
      damageType: "magic",
      defenseType: "light",
      armor: 8,
      reward: 400,
      size: 22,
      experienceReward: 420,
      leavesCorpse: false,
    },
    color: "#DC143C",
    portraitColor: "#8B0A2A",
    description: "Small demonic creatures that hurl fireballs from afar.",
    spriteConfig: imp1SpriteConfig,
    variants: {
      elite: { spriteConfig: imp2SpriteConfig, name: "Warlock Imp", displayName: "Warlock Imp" },
      unique: { spriteConfig: imp3SpriteConfig, name: "Imp Overlord", displayName: "Imp Overlord" },
    },
    eliteAbility: impEliteAbility,
  },
}
